import {
  Button,
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  CurrencyInput,
  cn,
} from "web-design-system";
import type { BackTestResponse } from "creso-core/types";
import {
  useBacktestedFundBasketData,
  useFundBasketAnalytics,
} from "creso-core/hooks";
import {
  convertToCurrency,
  formatAsCrore,
  formatAsPercentage,
} from "creso-core/utils";
import { useId, useMemo, useRef, useState } from "react";
import { Area, ComposedChart, Line, XAxis, YAxis } from "recharts";
import type { TooltipProps } from "recharts";
import { format } from "date-fns";
import { ArrowDownIcon, ArrowUpIcon } from "lucide-react";

const BACKTEST_PERIOD_OPTIONS = [
  { label: "1 Year", value: "1Y" as const },
  { label: "3 Years", value: "3Y" as const },
  { label: "5 Years", value: "5Y" as const },
  { label: "10 Years", value: "10Y" as const },
];

export const BENCHMARK_OPTIONS = [
  { label: "Nifty 50", value: "NIFTY 50" },
  { label: "Nifty 200", value: "NIFTY 200" },
  { label: "Nifty 500", value: "NIFTY 500" },
  // { label: "Nifty Bank", value: "NIFTY BANK" },
  // { label: "Nifty FMCG", value: "NIFTY FMCG" },
  // { label: "Nifty Healthcare Index", value: "NIFTY HEALTHCARE INDEX" },
  // { label: "Nifty IT", value: "NIFTY IT" },
  { label: "Nifty Midcap 150", value: "NIFTY MIDCAP 150" },
  // { label: "Nifty Oil & Gas", value: "NIFTY OIL & GAS" },
  { label: "Nifty Smallcap 250", value: "NIFTY SMALLCAP 250" },
  // {
  //   label: "Nifty Composite G-Sec Index",
  //   value: "NIFTY COMPOSITE G-SEC INDEX",
  // },
] as const;

type BacktestPeriod = (typeof BACKTEST_PERIOD_OPTIONS)[number]["value"];

const BASKET_STROKE = "var(--chart-3)";
const BENCHMARK_STROKE = "var(--muted-foreground)";

const chartConfig = {
  basket: {
    label: "Basket portfolio",
    color: BASKET_STROKE,
  },
  benchmark: {
    label: "Benchmark",
    color: BENCHMARK_STROKE,
  },
} satisfies ChartConfig;

type SeriesPoint = {
  date: string;
  portfolio_value: number;
  total_invested: number;
};

type ChartPoint = {
  date: string;
  basket?: number;
  benchmark?: number;
  basket_invested?: number;
  benchmark_invested?: number;
};

function mergeChartSeries(
  basketSeries: SeriesPoint[],
  benchmarkSeries: SeriesPoint[]
): ChartPoint[] {
  const map = new Map<string, Omit<ChartPoint, "date">>();
  basketSeries.forEach((p) => {
    const cur = map.get(p.date) ?? {};
    cur.basket = p.portfolio_value;
    cur.basket_invested = p.total_invested;
    map.set(p.date, cur);
  });
  benchmarkSeries.forEach((p) => {
    const cur = map.get(p.date) ?? {};
    cur.benchmark = p.portfolio_value;
    cur.benchmark_invested = p.total_invested;
    map.set(p.date, cur);
  });
  return [...map.entries()]
    .sort((a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime())
    .map(([date, v]) => ({ date, ...v }));
}

export function FundBasketBacktestChart({ id }: { id: number }) {
  const uid = useId().replace(/:/g, "");

  const [initialAmount, setInitialAmount] = useState("1000");
  const [sipAmount, setSipAmount] = useState("1000");
  const [period, setPeriod] = useState<BacktestPeriod>("5Y");
  const [benchmark, setBenchmark] = useState("NIFTY 500");

  const submittedParams = useRef({
    initialAmount: 1000,
    sipAmount: 1000,
    period: "5Y" as BacktestPeriod,
    benchmark: "NIFTY 500",
  });
  const [queryParams, setQueryParams] = useState(submittedParams.current);

  const { data, isLoading } = useBacktestedFundBasketData({
    id,
    initialAmount: queryParams.initialAmount,
    sipAmount: queryParams.sipAmount,
    period: queryParams.period,
    benchmark: queryParams.benchmark,
  });

  const { data: analytics } = useFundBasketAnalytics({
    id,
    benchmark: queryParams.benchmark,
  });

  const latestInceptionDate = useMemo(() => {
    const schemes = analytics?.schemes ?? [];
    if (schemes.length === 0) return null;
    return schemes.reduce(
      (max, s) =>
        s.fund?.inception_date
          ? Math.max(max, new Date(s.fund.inception_date).getTime())
          : max,
      0
    );
  }, [analytics?.schemes]);

  const { chartStartDate, chartEndDate } = useMemo(() => {
    const endDate = new Date();
    if (!latestInceptionDate) {
      return { chartStartDate: undefined, chartEndDate: undefined };
    }
    let requestedStart: Date;
    switch (period) {
      case "1Y":
        requestedStart = new Date(new Date().setDate(endDate.getDate() - 365));
        break;
      case "3Y":
        requestedStart = new Date(
          new Date().setDate(endDate.getDate() - 365 * 3)
        );
        break;
      case "5Y":
        requestedStart = new Date(
          new Date().setDate(endDate.getDate() - 365 * 5)
        );
        break;
      case "10Y":
        requestedStart = new Date(
          new Date().setDate(endDate.getDate() - 365 * 10)
        );
        break;
      default:
        requestedStart = new Date(new Date().setDate(endDate.getDate() - 365));
    }
    const startDate = new Date(
      Math.max(latestInceptionDate, requestedStart.getTime())
    );
    return {
      chartStartDate: format(startDate, "MMM dd, yyyy"),
      chartEndDate: format(endDate, "MMM dd, yyyy"),
    };
  }, [latestInceptionDate, period]);

  const handleUpdate = () => {
    const next = {
      initialAmount: Number(initialAmount),
      sipAmount: Number(sipAmount),
      period,
      benchmark,
    };
    submittedParams.current = next;
    setQueryParams(next);
  };

  const chartPoints = useMemo(
    () =>
      mergeChartSeries(data?.basket_series ?? [], data?.benchmark_series ?? []),
    [data]
  );

  const hasChartPoints = chartPoints.length > 0;

  const benchmarkLabel =
    BENCHMARK_OPTIONS.find((opt) => opt.value === data?.benchmark_name)
      ?.label ??
    data?.benchmark_name ??
    queryParams.benchmark;

  const formatXAxisTick = (dateStr: string) => {
    try {
      return format(new Date(dateStr), "MMM yy");
    } catch {
      return "";
    }
  };

  return (
    <div className="flex flex-col rounded-lg border border-border">
      <div className="flex flex-row flex-wrap items-stretch gap-3 border-b border-border p-5 pt-3">
        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">
            Initial investment amount
          </span>
          <CurrencyInput
            className="h-9 w-44"
            value={initialAmount}
            onValueChange={(v) => setInitialAmount(v ?? "")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">
            Subsequent SIP amount
          </span>
          <CurrencyInput
            className="h-9 w-44"
            value={sipAmount}
            onValueChange={(v) => setSipAmount(v ?? "")}
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">Time period</span>
          <Select
            value={period}
            onValueChange={(v) => setPeriod(v as BacktestPeriod)}
          >
            <SelectTrigger className="h-9 w-44">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BACKTEST_PERIOD_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {chartStartDate && chartEndDate && (
            <span className="text-xs text-muted-foreground">
              Date overlap &ndash; {chartStartDate} to {chartEndDate}
            </span>
          )}
        </div>

        <div className="w-px bg-border mx-5" />

        <div className="flex flex-col gap-1.5">
          <span className="text-xs text-muted-foreground">Compare against</span>
          <Select value={benchmark} onValueChange={setBenchmark}>
            <SelectTrigger className="h-9 w-60">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {BENCHMARK_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <Button
          className="self-center"
          onClick={handleUpdate}
          disabled={isLoading}
        >
          Update
        </Button>
      </div>

      {hasChartPoints ? (
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-100 p-5 pb-0 w-full"
        >
          <ComposedChart
            data={chartPoints}
            margin={{ top: 8, right: 8, bottom: 28, left: 4 }}
          >
            <defs>
              <linearGradient
                id={`backtestBasketFill-${uid}`}
                x1="0"
                y1="0"
                x2="0"
                y2="1"
              >
                <stop offset="5%" stopColor={BASKET_STROKE} stopOpacity={0.4} />
                <stop
                  offset="95%"
                  stopColor={BASKET_STROKE}
                  stopOpacity={0.05}
                />
              </linearGradient>
            </defs>
            <YAxis hide domain={["auto", "auto"]} />
            <XAxis
              dataKey="date"
              domain={["dataMin", "dataMax"]}
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              tickFormatter={formatXAxisTick}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <ChartTooltip
              cursor={false}
              content={<BacktestTooltip benchmarkLabel={benchmarkLabel} />}
            />
            <Line
              type="monotone"
              dataKey="benchmark"
              stroke={BENCHMARK_STROKE}
              strokeWidth={1.5}
              strokeDasharray="3 3"
              dot={false}
              connectNulls
              isAnimationActive
            />
            <Area
              type="monotone"
              dataKey="basket"
              stroke={BASKET_STROKE}
              strokeWidth={2}
              fill={`url(#backtestBasketFill-${uid})`}
              dot={false}
              connectNulls
              isAnimationActive
            />
          </ComposedChart>
        </ChartContainer>
      ) : (
        <div className="flex h-[200px] items-center justify-center rounded-md border border-dashed border-border bg-muted/30 text-sm text-muted-foreground">
          {isLoading
            ? "Loading backtest data..."
            : "No backtest data available. Click Update to run a backtest."}
        </div>
      )}

      {data && (
        <BacktestStatsTable
          basketStats={data.basket_metrics}
          benchmarkStats={data.benchmark_metrics}
          benchmarkLabel={benchmarkLabel}
        />
      )}
    </div>
  );
}

function BacktestTooltip({
  active,
  payload,
  benchmarkLabel,
}: TooltipProps<number, string> & { benchmarkLabel: string }) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as ChartPoint | undefined;
  if (!point) return null;

  const parsed = Date.parse(point.date);
  const formattedDate = Number.isNaN(parsed)
    ? point.date
    : format(parsed, "MMM dd, yyyy");

  const invested = point.basket_invested ?? point.benchmark_invested;

  return (
    <div className="border-border/50 bg-background min-w-64 rounded-md border p-3 text-xs shadow">
      <div className="text-xs font-semibold text-foreground">
        {formattedDate}
      </div>
      {typeof invested === "number" && (
        <div className="mt-1.5 text-muted-foreground">
          Invested:{" "}
          <span className="text-foreground">
            {convertToCurrency(invested, 0)}
          </span>
        </div>
      )}
      <div className="my-3 h-px bg-border" />
      <div className="flex flex-col gap-2.5">
        <BacktestTooltipRow
          color={BASKET_STROKE}
          name="Basket portfolio"
          portfolioValue={point.basket}
          totalInvested={point.basket_invested}
        />
        <BacktestTooltipRow
          color={BENCHMARK_STROKE}
          name={benchmarkLabel}
          portfolioValue={point.benchmark}
          totalInvested={point.benchmark_invested}
        />
      </div>
    </div>
  );
}

function BacktestTooltipRow({
  color,
  name,
  portfolioValue,
  totalInvested,
}: {
  color: string;
  name: string;
  portfolioValue: number | undefined;
  totalInvested: number | undefined;
}) {
  const hasValue = typeof portfolioValue === "number";
  const hasReturn =
    hasValue && typeof totalInvested === "number" && totalInvested > 0;
  const returnPct = hasReturn
    ? ((portfolioValue - totalInvested) / totalInvested) * 100
    : undefined;
  const sign = hasReturn
    ? returnPct! > 0
      ? "+"
      : returnPct! < 0
        ? "-"
        : ""
    : "";

  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="h-2.5 w-2.5 shrink-0 rounded-[3px]"
        style={{ backgroundColor: color }}
      />
      <span className="flex-1 text-foreground">{name}</span>
      {hasValue && (
        <span className="font-medium tabular-nums text-foreground">
          {convertToCurrency(portfolioValue, 0)}
        </span>
      )}
      {hasReturn && (
        <span
          data-sign={sign}
          className={cn(
            "flex items-center gap-0.5 font-medium tabular-nums",
            "data-[sign='+']:text-constructive data-[sign='-']:text-destructive",
            sign === "" && "text-muted-foreground"
          )}
        >
          {sign === "+" && <ArrowUpIcon className="size-3" />}
          {sign === "-" && <ArrowDownIcon className="size-3" />}
          {Math.abs(returnPct!).toFixed(2)}%
        </span>
      )}
    </div>
  );
}

type MetricsStats = BackTestResponse["basket_metrics"];

function BacktestStatsTable({
  basketStats,
  benchmarkStats,
  benchmarkLabel,
}: {
  basketStats: MetricsStats;
  benchmarkStats: MetricsStats;
  benchmarkLabel: string;
}) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-1/3" />
          <TableHead className="text-left">
            <div className="flex items-center justify-start gap-2">
              <span className="inline-block size-3 rounded-sm bg-chart-3" />
              <span className="text-sm font-medium">Basket portfolio</span>
            </div>
          </TableHead>
          <TableHead className="text-left">
            <div className="flex items-center justify-start gap-2">
              <span className="inline-block w-4 border-t-2 border-dashed border-muted-foreground" />
              <span className="text-sm font-medium">{benchmarkLabel}</span>
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        <TableRow>
          <TableCell className="font-medium">Total invested</TableCell>
          <TableCell className="text-left">
            {formatAsCrore(basketStats.total_invested)}
          </TableCell>
          <TableCell className="text-left">
            {formatAsCrore(benchmarkStats.total_invested)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="font-medium">Final value</TableCell>
          <TableCell className="text-left">
            {formatAsCrore(basketStats.final_value)}
          </TableCell>
          <TableCell className="text-left">
            {formatAsCrore(benchmarkStats.final_value)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="font-medium">XIRR</TableCell>
          <TableCell
            className={cn(
              "text-left",
              basketStats.xirr > 0 ? "text-constructive" : "text-destructive",
              basketStats.xirr === 0 && "text-muted-foreground"
            )}
          >
            {basketStats.xirr > 0 ? "+" : basketStats.xirr < 0 ? "-" : ""}
            {formatAsPercentage(basketStats.xirr, "-", 1)}
          </TableCell>
          <TableCell
            className={cn(
              "text-left",
              benchmarkStats.xirr > 0
                ? "text-constructive"
                : "text-destructive",
              benchmarkStats.xirr === 0 && "text-muted-foreground"
            )}
          >
            {benchmarkStats.xirr > 0 ? "+" : benchmarkStats.xirr < 0 ? "-" : ""}
            {formatAsPercentage(benchmarkStats.xirr, "-", 1)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="font-medium">Standard deviation</TableCell>
          <TableCell className="text-left">
            {basketStats.standard_deviation.toFixed(2)}
          </TableCell>
          <TableCell className="text-left">
            {benchmarkStats.standard_deviation.toFixed(2)}
          </TableCell>
        </TableRow>
        <TableRow>
          <TableCell className="font-medium">Max drawdown</TableCell>
          <TableCell className="text-left">
            <div className="flex flex-col gap-1">
              <span className="text-sm">
                {formatAsPercentage(basketStats.max_drawdown, "-", 2)}
              </span>
              {basketStats.max_drawdown_date ? (
                <span className="text-xs text-muted-foreground">
                  {format(basketStats.max_drawdown_date, "MMM dd, yyyy")}
                </span>
              ) : null}
            </div>
          </TableCell>
          <TableCell className="text-left">
            <div className="flex flex-col gap-1">
              <span className="text-sm">
                {formatAsPercentage(benchmarkStats.max_drawdown, "-", 2)}
              </span>
              {benchmarkStats.max_drawdown_date ? (
                <span className="text-xs text-muted-foreground">
                  {format(benchmarkStats.max_drawdown_date, "MMM dd, yyyy")}
                </span>
              ) : null}
            </div>
          </TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
